import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api-client';
import { API_BASE } from '../api-base';
import { queryKeys } from '../query-client';
import type { TranscriptResponse } from '../../components/symphony/tasks/agent-conversation-rows';
import type { ActionRow } from './map-events';
import { buildWorldRecap, recapHasUnknownStart, RECAP_DAY_MS, type RecapTask, type RecapTranscript } from './world-recap';

export function useWorldRecap(until: number | null, tasks: RecapTask[], ready: boolean) {
  const taskIds = tasks.filter(task => new Date(task.updatedDate || task.createdDate || '').getTime() >= (until ?? 0) - RECAP_DAY_MS).map(task => task.id).sort();
  return useQuery({
    // A recap is a snapshot. Live task updates must not restart its read or shift its playhead.
    queryKey: queryKeys.homeWorldRecap(until),
    enabled: until !== null && ready,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      const end = until!;
      const actions: ActionRow[] = []; let cursor: string | null = null;
      const seen = new Set<string>();
      for (let page = 0; page < 10; page++) {
        const params = new URLSearchParams({ limit: '100' });
        if (cursor) params.set('cursor', cursor);
        else params.set('since', new Date(end - RECAP_DAY_MS).toISOString());
        const response = await apiFetch(`${API_BASE}/api/agent-actions?${params}`, { credentials: 'include', signal });
        if (!response.ok) throw new Error(`Activity request failed (${response.status})`);
        const data = await response.json() as { actions?: ActionRow[]; cursor?: string | null };
        actions.push(...data.actions ?? []); cursor = data.cursor || null;
        if (!cursor) break;
        if (seen.has(cursor)) throw new Error('Activity pagination did not advance');
        seen.add(cursor);
      }
      const relatedIds = new Set([...taskIds, ...actions.map(action => action.relatedTaskId).filter(Boolean)]);
      const historyTasks = tasks.filter(task => relatedIds.has(task.id));
      const candidates = tasks.filter(task => relatedIds.has(task.id) && (task.agentConversationId || task.reviewerConversationId));
      const transcripts: RecapTranscript[] = []; let partial = !!cursor || candidates.length > 30 || historyTasks.length > 30;
      const histories = new Map<string, RecapTask['changeLog']>();
      for (let start = 0; start < Math.min(historyTasks.length, 30); start += 3) {
        const batch = await Promise.allSettled(historyTasks.slice(start, Math.min(start + 3, 30)).map(async task => {
          const response = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(task.id)}`, { credentials: 'include', signal });
          if (!response.ok) throw new Error(`Task history request failed (${response.status})`);
          const data = await response.json() as { task?: { changeLog?: RecapTask['changeLog'] } };
          if (!Array.isArray(data.task?.changeLog)) throw new Error('Task history unavailable');
          return { id: task.id, changeLog: data.task.changeLog };
        }));
        for (const result of batch) {
          if (result.status === 'fulfilled') histories.set(result.value.id, result.value.changeLog);
          else { if (signal?.aborted) throw result.reason; partial = true; }
        }
      }
      // Bounded, on-demand reads. The existing endpoint returns sanitized user-visible steps only.
      for (let start = 0; start < Math.min(candidates.length, 30); start += 3) {
        const batch = await Promise.allSettled(candidates.slice(start, Math.min(start + 3, 30)).map(async task => {
          const response = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(task.id)}/agent-conversation?includeReviewer=1`, { credentials: 'include', signal });
          if (!response.ok) throw new Error(`Transcript request failed (${response.status})`);
          return { task, transcript: await response.json() as TranscriptResponse };
        }));
        for (const result of batch) {
          if (result.status === 'fulfilled') {
            transcripts.push(result.value);
            if (result.value.transcript.reviewerUnavailable) partial = true;
            if ((result.value.transcript.messages?.length ?? 0) >= 300 || (result.value.transcript.reviewerMessages?.length ?? 0) >= 300) partial = true;
          } else { if (signal?.aborted) throw result.reason; partial = true; }
        }
      }
      const frozenTasks = tasks.map(task => ({ ...task, changeLog: histories.get(task.id) ?? task.changeLog }));
      const events = buildWorldRecap(actions, frozenTasks, transcripts, end);
      return { events, tasks: frozenTasks, partial: partial || recapHasUnknownStart(events) };
    },
  });
}
